<template>
  <div ref="wurzel" class="schrift-waehler" @keydown.esc="offen = false">
    <button
      type="button"
      class="schrift-waehler-feld"
      :aria-expanded="offen ? 'true' : 'false'"
      data-test="schrift-waehler-feld"
      @click="umschalten"
    >
      <span :style="schriftStil(value)">{{ value || t('keine') }}</span>
      <span aria-hidden="true">▾</span>
    </button>

    <div v-if="offen" class="schrift-waehler-liste" data-test="schrift-waehler-liste">
      <input
        ref="suche"
        v-model="suche"
        type="search"
        class="form-control form-control-sm mb-2"
        :placeholder="t('suchen')"
      >
      <div class="schrift-waehler-filter mb-2">
        <button
          v-for="q in quellen"
          :key="q"
          type="button"
          :class="{ an: filter === q }"
          @click="filter = q"
        >{{ t(`quelle_${q}`) }}</button>
      </div>
      <button
        type="button"
        class="schrift-waehler-hochladen mb-2"
        data-test="schrift-waehler-hochladen"
        @click="hochladen"
      >+ {{ t('hochladen') }}</button>
      <ul @scroll="weiterLaden">
        <li v-for="s in treffer" :key="`${s.quelle}-${s.name}`">
          <button
            type="button"
            :class="{ gewaehlt: s.name === value }"
            @click="waehlen(s)"
          >
            <span :style="schriftStil(s.name)">{{ s.name }}</span>
            <small>{{ quelleText(s) }}</small>
          </button>
        </li>
        <li v-if="!treffer.length" class="leer">{{ t('nichtsGefunden') }}</li>
        <li v-else-if="treffer.length < passend.length" class="leer">
          {{ $t('settings.ciBrandKit.schriftWaehler.mehr', { n: treffer.length, gesamt: passend.length }) }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
// One picker for every font source of the CI (spec doc "Schriften-Upload:
// Alternativen", way B): search, filter by source, upload right here.
import { ref } from 'vue';

const GOOGLE_KEY = 'AIzaSyCBBJElCVDbMFMth4hmBGIAzDp5bO8ozWY';
// The Google list has ~1900 families and each shown one loads a preview:
// the list grows by this step while scrolling.
const SCHRITT = 60;

// Loaded once per page, shared by both pickers.
const googleListe = ref(null);
let googleLaden = null;
const vorschauGeladen = new Set();

function googleHolen() {
  if (!googleLaden) {
    googleLaden = fetch(`https://www.googleapis.com/webfonts/v1/webfonts?key=${GOOGLE_KEY}&sort=popularity`)
      .then((r) => r.json())
      .then((d) => { googleListe.value = (d.items || []).map((i) => i.family); })
      .catch(() => { googleListe.value = []; });
  }
  return googleLaden;
}

// Only the letters of the name: a few hundred bytes per font.
function googleVorschau(name) {
  if (vorschauGeladen.has(name)) return;
  vorschauGeladen.add(name);
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(name)}&text=${encodeURIComponent(name)}&display=swap`;
  document.head.appendChild(link);
}

export default {
  name: 'SchriftWaehler',
  props: {
    value: { type: String, default: '' },
    eigene: { type: Array, default: () => [] },
    eigeneStaerken: { type: Object, default: () => ({}) },
    adobe: { type: Array, default: () => [] },
    system: { type: Array, default: () => [] },
    // Fonts already in this CI (e.g. Google fonts of the font slots).
    vorhandene: { type: Array, default: () => [] },
  },
  data: () => ({
    offen: false,
    suche: '',
    filter: 'alle',
    anzahl: SCHRITT,
    quellen: ['alle', 'eigene', 'google', 'adobe', 'system'],
  }),
  computed: {
    alle() {
      const liste = [];
      const gesehen = new Set();
      const dazu = (name, quelle) => {
        if (!name || gesehen.has(name)) return;
        gesehen.add(name);
        liste.push({ name, quelle });
      };
      this.eigene.forEach((n) => dazu(n, 'eigene'));
      this.adobe.forEach((n) => dazu(n, 'adobe'));
      this.system.forEach((n) => dazu(n, 'system'));
      this.vorhandene.forEach((n) => dazu(n, 'google'));
      (googleListe.value || []).forEach((n) => dazu(n, 'google'));
      return liste;
    },
    passend() {
      const q = this.suche.trim().toLowerCase();
      return this.alle.filter((s) => (this.filter === 'alle' || s.quelle === this.filter)
        && (!q || s.name.toLowerCase().includes(q)));
    },
    treffer() {
      return this.passend.slice(0, this.anzahl);
    },
  },
  watch: {
    suche() { this.anzahl = SCHRITT; },
    filter() { this.anzahl = SCHRITT; },
    treffer(liste) {
      liste.filter((s) => s.quelle === 'google' && !this.vorhandeneAnders(s.name)).forEach((s) => googleVorschau(s.name));
    },
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.aussen);
  },
  methods: {
    t(key) {
      return this.$t(`settings.ciBrandKit.schriftWaehler.${key}`);
    },
    vorhandeneAnders(name) {
      return this.eigene.includes(name) || this.system.includes(name) || this.adobe.includes(name);
    },
    umschalten() {
      this.offen = !this.offen;
      if (this.offen) {
        googleHolen();
        document.addEventListener('mousedown', this.aussen);
        this.$nextTick(() => this.$refs.suche && this.$refs.suche.focus());
      } else {
        document.removeEventListener('mousedown', this.aussen);
      }
    },
    weiterLaden(e) {
      const ul = e.target;
      if (ul.scrollTop + ul.clientHeight >= ul.scrollHeight - 80 && this.anzahl < this.passend.length) {
        this.anzahl += SCHRITT;
      }
    },
    aussen(e) {
      if (this.$refs.wurzel && !this.$refs.wurzel.contains(e.target)) this.umschalten();
    },
    waehlen(s) {
      this.$emit('input', s.name, s.quelle);
      this.umschalten();
    },
    hochladen() {
      this.$emit('hochladen');
      this.umschalten();
    },
    quelleText(s) {
      if (s.quelle !== 'eigene') return this.t(`quelle_${s.quelle}`);
      const staerken = [...(this.eigeneStaerken[s.name] || [])].sort((a, b) => a - b);
      return [this.t('quelle_eigene'), staerken.join(', ')].filter(Boolean).join(' · ');
    },
    schriftStil(name) {
      return name ? { fontFamily: `'${name}', sans-serif` } : {};
    },
  },
};
</script>

<style scoped>
.schrift-waehler {
  position: relative;
}

.schrift-waehler-feld {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 6px 12px;
  font-size: 16px;
  text-align: left;
  color: inherit;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 4px;
}

.schrift-waehler-liste {
  position: absolute;
  z-index: 20;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  min-width: 320px;
  padding: 10px;
  background: #3f4347;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
}

.schrift-waehler-filter {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.schrift-waehler-filter button {
  padding: 2px 10px;
  font-size: 12px;
  color: inherit;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
}

.schrift-waehler-filter button.an {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.7);
}

.schrift-waehler-hochladen {
  width: 100%;
  padding: 8px;
  font-weight: 600;
  color: inherit;
  background: transparent;
  border: 1px dashed rgba(255, 255, 255, 0.5);
  border-radius: 4px;
}

.schrift-waehler-liste ul {
  max-height: 280px;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  list-style: none;
}

.schrift-waehler-liste li button {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  width: 100%;
  padding: 6px 8px;
  font-size: 17px;
  color: inherit;
  text-align: left;
  background: transparent;
  border: 0;
  border-radius: 4px;
}

.schrift-waehler-liste li button:hover,
.schrift-waehler-liste li button.gewaehlt {
  background: rgba(255, 255, 255, 0.1);
}

.schrift-waehler-liste li small {
  margin-left: 12px;
  font-family: inherit;
  font-size: 11px;
  opacity: 0.7;
  white-space: nowrap;
}

.schrift-waehler-liste li.leer {
  padding: 6px 8px;
  font-size: 13px;
  opacity: 0.7;
}
</style>
